import { useState, useEffect } from 'react'
import { Outlet, NavLink } from 'react-router-dom'
import { LogOut, Bell, Search } from 'lucide-react'
import { signOut } from '@fleetops/firebase'
import { Avatar, NolensMark, NolensWordmark, cn } from '@fleetops/ui'
import { useSession } from '@/store/auth.store'
import { hasMinimumRole } from '@/lib/rbac'
import { useNavigate } from 'react-router-dom'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useTrailersCount } from '@/features/trailers/hooks/useTrailersCount'
import { useDashboardSummary } from '@/features/dashboard/hooks/useAggregates'
import { useNotifications } from '@/features/notifications/hooks/useNotifications'
import { useMarkAllNotificationsRead } from '@/features/notifications/hooks/useNotificationMutations'
import { NotificationPanel } from '@/features/notifications/components/NotificationPanel'
import { GlobalSearch } from '@/features/search/GlobalSearch'

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Administrador',
  tenant_admin: 'Administrador',
  fleet_manager: 'Gestor de Frota',
  operator: 'Operador',
  viewer: 'Visualizador',
  supplier: 'Fornecedor',
}

type NavItemDef = {
  to: string
  letter: string
  label: string
  countKey?: 'damages' | 'trailers'
  minimumRole?: string
}

const topItems: NavItemDef[] = [
  { to: '/command-center', letter: 'C', label: 'Centro de Comando' },
  { to: '/dashboard', letter: 'D', label: 'Dashboard' },
  { to: '/insights', letter: 'I', label: 'Insights' },
  { to: '/trailers', letter: 'F', label: 'Frota', countKey: 'trailers' },
]

const moduleItems: NavItemDef[] = [
  { to: '/assistance', letter: 'A', label: 'Assistência' },
  { to: '/compliance', letter: 'C', label: 'Conformidade' },
  { to: '/damages', letter: 'Av', label: 'Avarias', countKey: 'damages' },
  { to: '/maintenance', letter: 'M', label: 'Manutenção' },
  { to: '/costs', letter: '€', label: 'Custos' },
  { to: '/suppliers', letter: 'F', label: 'Fornecedores', minimumRole: 'fleet_manager' },
  { to: '/invoices', letter: 'F', label: 'Faturas', minimumRole: 'fleet_manager' },
]

const workspaceItems: NavItemDef[] = [
  { to: '/files', letter: 'F', label: 'Ficheiros' },
  { to: '/approvals', letter: 'A', label: 'Aprovações', minimumRole: 'fleet_manager' },
]

function NavIcon({ letter, active }: { letter: string; active: boolean }) {
  return (
    <div className={cn(
      'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold transition-colors',
      active ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
    )}>
      {letter}
    </div>
  )
}

function SidebarNavLink({
  item,
  count,
}: {
  item: NavItemDef
  count?: number
}) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors',
          'lg:px-2.5',
          isActive
            ? 'bg-white/10 text-white'
            : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
        )
      }
    >
      {({ isActive }) => (
        <>
          <NavIcon letter={item.letter} active={isActive} />
          <span className="sr-only lg:not-sr-only lg:flex-1">{item.label}</span>
          {count !== undefined && count > 0 && (
            <span className="hidden rounded-full bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-300 lg:block">
              {count}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}

function SectionLabel({ label }: { label: string }) {
  return (
    <p className="mb-1 mt-3 hidden px-2.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 lg:block">
      {label}
    </p>
  )
}

export function AppLayout() {
  const session = useSession()
  const navigate = useNavigate()
  // O(1) rollup doc instead of a full damages listener — every page rendered
  // this layout used to subscribe to the whole tenant's damages collection
  // just to show a sidebar badge count.
  const { summary: damagesSummary } = useDashboardSummary()
  const trailersCount = useTrailersCount()
  const [showNotifications, setShowNotifications] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const { notifications, unreadCount } = useNotifications()
  const markAllRead = useMarkAllNotificationsRead()
  // Escalations are the one notification type someone is actively waiting
  // on a decision for — worth standing out from routine SLA/compliance
  // noise in the bell badge itself, not just once the panel is opened.
  const hasUnreadEscalation = notifications.some((n) => !n.read && n.type === 'estimate_escalated')

  // The settings links at the bottom all point at guarded routes. Without
  // this, a gestor de frota saw "Utilizadores", "Alertas", "Emails" and
  // "Auto-aprovação" and got bounced by ProtectedRoute on every one of them,
  // and "Bases" was shown to operadores who cannot open it either.
  const isTenantAdmin = Boolean(session?.role && hasMinimumRole(session.role, 'tenant_admin'))
  const isFleetManager = Boolean(session?.role && hasMinimumRole(session.role, 'fleet_manager'))

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearch((v) => !v)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const counts: Record<string, number> = {
    damages: damagesSummary?.total ?? 0,
    trailers: trailersCount ?? 0,
  }

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="flex w-14 flex-col border-r border-slate-800 bg-slate-900 py-4 lg:w-56 lg:px-3 [&_:focus-visible]:ring-accent-400 [&_:focus-visible]:ring-offset-slate-900">
        {/* Brand */}
        <div className="mb-4 flex items-center gap-2.5 px-2 lg:px-0">
          <NolensMark size={32} />
          <div className="sr-only lg:not-sr-only">
            <p className="text-sm text-white">
              <NolensWordmark product="fleet" />
            </p>
          </div>
        </div>

        {/* Search */}
        <button
          onClick={() => setShowSearch(true)}
          className="mb-3 flex h-8 w-full items-center gap-2.5 rounded-lg border border-slate-700 bg-slate-800/60 px-2 text-slate-400 transition-colors hover:border-slate-600 hover:text-slate-300 lg:px-2.5"
        >
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="sr-only lg:not-sr-only lg:flex-1 lg:text-left lg:text-xs">Pesquisar…</span>
          <kbd aria-hidden className="hidden rounded border border-slate-700 px-1 py-0.5 text-[9px] text-slate-400 lg:block">⌘K</kbd>
        </button>

        {/* Top nav */}
        <nav className="flex flex-col gap-0.5">
          {topItems.map((item) => (
            <SidebarNavLink
              key={item.to}
              item={item}
              count={item.countKey ? counts[item.countKey] : undefined}
            />
          ))}
        </nav>

        {/* Módulos */}
        <SectionLabel label="Módulos" />
        <nav className="flex flex-col gap-0.5">
          {moduleItems.filter((item) =>
            !item.minimumRole || (session?.role && hasMinimumRole(session.role, item.minimumRole as Parameters<typeof hasMinimumRole>[1]))
          ).map((item) => (
            <SidebarNavLink
              key={item.to}
              item={item}
              count={item.countKey ? counts[item.countKey] : undefined}
            />
          ))}
        </nav>

        {/* Espaço de Trabalho */}
        <SectionLabel label="Espaço de Trabalho" />
        <nav className="flex flex-col gap-0.5">
          {workspaceItems.filter((item) =>
            !item.minimumRole || (session?.role && hasMinimumRole(session.role, item.minimumRole as Parameters<typeof hasMinimumRole>[1]))
          ).map((item) => (
            <SidebarNavLink key={item.to} item={item} />
          ))}
        </nav>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom section */}
        <div className="flex flex-col gap-1 border-t border-slate-800 pt-3">
          {/* Notification bell */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications((v) => !v)}
              aria-expanded={showNotifications}
              className={cn(
                'flex h-8 w-full items-center gap-2.5 rounded-lg px-2 text-sm font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200 lg:px-2.5',
                showNotifications && 'bg-slate-800 text-slate-200',
              )}
            >
              <div className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-800 text-[11px] font-bold">
                <Bell className="h-3.5 w-3.5" />
                {unreadCount > 0 && (
                  <span
                    title={hasUnreadEscalation ? 'Inclui orçamento(s) escalado(s)' : undefined}
                    className={cn(
                      'absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold text-white',
                      hasUnreadEscalation ? 'bg-amber-500' : 'bg-red-500',
                    )}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="sr-only lg:not-sr-only">Notificações</span>
            </button>

            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="absolute bottom-0 left-full z-50 ml-2">
                  <NotificationPanel
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onMarkAllRead={() =>
                      markAllRead.mutate(notifications.filter((n) => !n.read).map((n) => n.id))
                    }
                    onMarkRead={(id) => markAllRead.mutate([id])}
                    onOpen={(n) => {
                      const target = n.damageId
                        ? `/damages/${n.damageId}`
                        : n.maintenanceId ? `/maintenance#${n.maintenanceId}`
                        : n.invoiceId ? '/invoices'
                        : n.type === 'compliance_alert' ? '/compliance'
                        : n.type === 'user_invited' ? '/settings/users'
                        : null
                      if (!target) return
                      setShowNotifications(false)
                      navigate(target)
                    }}
                    onClose={() => setShowNotifications(false)}
                  />
                </div>
              </>
            )}
          </div>

          {isTenantAdmin && (
            <NavLink
              to="/settings/users"
              className={({ isActive }) =>
                cn(
                  'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors lg:px-2.5',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold',
                    isActive ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
                  )}>
                    U
                  </div>
                  <span className="sr-only lg:not-sr-only">Utilizadores</span>
                </>
              )}
            </NavLink>
          )}
          {isFleetManager && (
            <NavLink
              to="/settings/fleets"
              className={({ isActive }) =>
                cn(
                  'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors lg:px-2.5',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold',
                    isActive ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
                  )}>
                    B
                  </div>
                  <span className="sr-only lg:not-sr-only">Bases</span>
                </>
              )}
            </NavLink>
          )}
          {isTenantAdmin && (
            <NavLink
              to="/settings/notifications"
              className={({ isActive }) =>
                cn(
                  'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors lg:px-2.5',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold',
                    isActive ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
                  )}>
                    N
                  </div>
                  <span className="sr-only lg:not-sr-only">Alertas</span>
                </>
              )}
            </NavLink>
          )}
          {isTenantAdmin && (
            <NavLink
              to="/settings/emails"
              className={({ isActive }) =>
                cn(
                  'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors lg:px-2.5',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold',
                    isActive ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
                  )}>
                    E
                  </div>
                  <span className="sr-only lg:not-sr-only">Emails</span>
                </>
              )}
            </NavLink>
          )}
          {isTenantAdmin && (
            <NavLink
              to="/settings/auto-approval"
              className={({ isActive }) =>
                cn(
                  'group flex h-8 items-center gap-2.5 rounded-lg px-2 text-sm font-medium transition-colors lg:px-2.5',
                  isActive
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <div className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold',
                    isActive ? 'bg-accent-400 text-slate-900' : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700 group-hover:text-slate-200',
                  )}>
                    IA
                  </div>
                  <span className="sr-only lg:not-sr-only">Auto-aprovação</span>
                </>
              )}
            </NavLink>
          )}

          {session && (
            <div className="flex flex-col items-center gap-2 rounded-lg px-2 py-1.5 lg:flex-row lg:px-2.5">
              <Avatar name={session.email} size="sm" />
              <div className="hidden min-w-0 flex-1 lg:block">
                <p className="truncate text-xs font-medium text-slate-300">{session.email}</p>
                <p className="text-[10px] text-slate-400">{ROLE_LABELS[session.role] ?? session.role}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:text-slate-200"
                title="Terminar sessão"
                aria-label="Terminar sessão"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          )}
          <p className="hidden px-2.5 pt-1 text-[10px] text-slate-400 lg:block">
            Desenvolvido por rdoria.com
          </p>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex flex-1 flex-col overflow-hidden">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
    </div>
  )
}
